import { SVGIcon } from "#/ui/svg-icon";
import {
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { X } from "lucide-react";
import React, { ReactElement } from "react";
import { toast as sonnerToaster } from "sonner";
import { hapticError, hapticSuccess, hapticWarning } from "#/lib/haptics";

const ToastContainer = ({
  text,
  icon,
  button,
  toastId,
}: {
  text: string;
  icon: React.ReactNode;
  button?: React.ReactNode;
  toastId: string | number;
}) => (
  <div
    role="status"
    aria-live="polite"
    className="z-100 flex min-h-16 w-[calc(100vw-2rem)] max-w-[20rem] items-center justify-between rounded-xl bg-secondary px-3 py-2"
  >
    <div className="mr-3 flex min-w-0 items-center">
      <div>{icon && icon}</div>
      <p className="line-clamp-4 min-w-0 text-sm font-circular-std font-600 text-secondary-foreground md:text-base">
        {text}
      </p>
    </div>
    {button || (
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={() => sonnerToaster.dismiss(toastId)}
        className="flex h-11 w-11 shrink-0 touch-manipulation items-center justify-center rounded-lg text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X aria-hidden="true" className="h-4 w-4" />
      </button>
    )}
  </div>
);

export const toast = {
  /**
   * A fully custom toast body (e.g. components/action-toast.tsx). Rendered
   * inside sonner so the body can dismiss itself by id; keeps `sonner`
   * imported only here.
   */
  custom: (
    render: (toastId: string | number) => ReactElement,
    options?: { id?: string | number; duration?: number },
  ) => {
    hapticSuccess();
    return sonnerToaster.custom(render, options);
  },
  success: (text: string, button?: ReactElement) => {
    hapticSuccess();
    return sonnerToaster.custom((toastId) => (
      <ToastContainer
        text={text}
        button={button}
        toastId={toastId}
        icon={<SVGIcon svg="success" className="h-8 w-8 mr-3" />}
      />
    ));
  },
  error: (text: string, button?: ReactElement, options?: { id?: string }) => {
    hapticError();
    return sonnerToaster.custom(
      (toastId) => (
        <ToastContainer
          text={text}
          button={button}
          toastId={toastId}
          icon={<XCircleIcon className="h-9 text-red-500 w-9 mr-2" />}
        />
      ),
      { id: options?.id },
    );
  },
  warn: (text: string, button?: ReactElement) => {
    hapticWarning();
    return sonnerToaster.custom((toastId) => (
      <ToastContainer
        text={text}
        button={button}
        toastId={toastId}
        icon={
          <ExclamationTriangleIcon className="h-9 w-9 text-yellow-500 mr-2" />
        }
      />
    ));
  },
  /** Dismiss a toast by id, or all toasts when no id is given. */
  dismiss: (id?: string | number) => sonnerToaster.dismiss(id),
};
